import type { RecoveryFlow, UpdateRecoveryFlowBody } from '@ory/client';
import { oryKratosClient } from '../ory-kratos.client';
import { getErrorResponseData, is400Error, isFlowExpired, type ServiceResponse } from './types';
export const recoveryService = {
  /**
   * リカバリーフロー（パスワードをお忘れですか？）を初期化
   *
   * @remarks
   * `returnTo` は Kratos 側の許可リスト設定に依存して 400 になり得るため、
   * 原則フロント側で画面遷移を制御します（sign-in.service と同方針）。
   */
  async initFlow(options?: {
    returnTo?: string;
  }): Promise<RecoveryFlow> {
    const {
      data
    } = await oryKratosClient.getApi().createBrowserRecoveryFlow({
      returnTo: options?.returnTo
    });
    if (!data) {
      throw new Error('Failed to create recovery flow: No data returned');
    }
    return data;
  },
  /**
   * 既存のリカバリーフローを取得（メールのリンク遷移など）
   */
  async getFlow(flowId: string): Promise<RecoveryFlow> {
    const {
      data
    } = await oryKratosClient.getApi().getRecoveryFlow({
      id: flowId
    });
    if (!data) {
      throw new Error('Failed to get recovery flow: No data returned');
    }
    return data;
  },
  /**
   * リカバリーフローを送信
   *
   * @returns
   * - バリデーションエラー(400): `data` に更新後の RecoveryFlow を返す
   * - それ以外のエラー: `error` に入れて返す
   */
  async submitFlow(flowId: string, body: UpdateRecoveryFlowBody): Promise<ServiceResponse<RecoveryFlow>> {
    try {
      const {
        data
      } = await oryKratosClient.getApi().updateRecoveryFlow({
        flow: flowId,
        updateRecoveryFlowBody: body
      });
      if (!data) {
        return {
          data: null,
          error: null
        };
      }
      return {
        data,
        error: null
      };
    } catch (err) {
      if (is400Error(err)) {
        const flowData = getErrorResponseData<RecoveryFlow>(err);
        return {
          data: flowData,
          error: null
        };
      }

      // self_service_flow_expired (410): 新しいフローを発行して UI 側で再開できるようにする
      if (isFlowExpired(err)) {
        try {
          const newFlow = await recoveryService.initFlow();
          return {
            data: newFlow,
            error: null,
            flowExpired: true
          };
        } catch (reinitError) {
          return {
            data: null,
            error: reinitError as Error
          };
        }
      }
      return {
        data: null,
        error: err as Error
      };
    }
  }
};